import Delegate from 'ftdomdelegate'
import gsap, {Cubic} from 'gsap'

import Drawer from './drawer'
import Dom from '../tools/dom'
import ShippingEstimator from './shipping-estimator'
import Currency from '../tools/currency'

export default class CartSection {
  constructor(container) {

    this.element = container;
    this.delegateElement = new Delegate(this.element);
    this.options = JSON.parse(this.element.getAttribute('data-section-settings'));
    this.itemCount = this.options['itemCount'];
    this.isCartNoteOpen = false;

    if (this.options['drawer']) {
      this.sidebarDrawer = new Drawer(this.element, {
        onClose: this._onDrawerClosed.bind(this)
      });
    }

    if (this.options['hasShippingEstimator']) {
      this.shippingEstimator = new ShippingEstimator(this.element.querySelector('.shippingEstimator'));
    }

    this._attachListeners();
  }

  onUnload() {
    if (this.options['hasShippingEstimator']) {
      this.shippingEstimator.destroy();
    }

    this.delegateElement.off();
    document.removeEventListener('product:added', this._onProductAddedListener);
  }
  _attachListeners() {
    this._onProductAddedListener = this._onProductAdded.bind(this);

    this.delegateElement.on('change', '#cart-note', this._updateCartNote.bind(this));

    if (this.options['type'] !== 'page') {
      this.delegateElement.on('click', '[data-action="update-item-quantity"], [data-action="remove-item"]', this._updateItemQuantity.bind(this));
      this.delegateElement.on('change', '.quantitySelector__currentQuantity', this._updateItemQuantity.bind(this));
    } else {
      this.delegateElement.on('change', '.quantitySelector__currentQuantity', this._reloadPageWithQuantity.bind(this));
    }

    // We have some listeners that are specific to the fact it's a drawer or the dedicated cart page
    if (this.options['drawer']) {
      this.delegateElement.on('click', '[data-action="toggle-cart-note"]', this._toggleCartNote.bind(this));
    }

    document.addEventListener('product:added', this._onProductAddedListener);
  }
  _updateCartNote(event, target) {
    fetch('/cart/update.js', {
      body: JSON.stringify({ note: target.value }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
      }
    });
  }
  _toggleCartNote() {
    var _this29 = this;

    var noteContainer = this.element.querySelector('.cart__offscreenNoteContainer'),
        cartNote = this.element.querySelector('#cart-note');

    this.element.classList.toggle('has-note-open');
    this.element.querySelector('.cart__noteButton').innerHTML = cartNote.value !== '' ? window.languages.cartEditNote : window.languages.cartAddNote;

    noteContainer.setAttribute('aria-hidden', noteContainer.getAttribute('aria-hidden') === 'true' ? 'false' : 'true');

    this.isCartNoteOpen = noteContainer.getAttribute('aria-hidden') === 'false';

    if (this.element.classList.contains('has-note-open')) {
      var transitionEndListener = function transitionEndListener() {
        _this29.element.querySelector('#cart-note').focus();
        noteContainer.removeEventListener('transitionend', transitionEndListener);
      };

      noteContainer.addEventListener('transitionend', transitionEndListener);
    }
  }
  _updateItemQuantity(event, target) {
    var _this30 = this;

    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var quantity = null,
        elementToAnimate = null;

    if (target.tagName === 'INPUT') {
      quantity = parseInt(Math.max(parseInt(target.value) || 1, 1));
    } else {
      quantity = parseInt(target.getAttribute('data-quantity'));
    }

    // If the quantity is 0, then we will animate the product with a removal effect
    if (quantity === 0) {
      elementToAnimate = target.closest('.cartItemWrapper');
    }

    fetch('/cart/change.js', {
      body: JSON.stringify({ id: target.getAttribute('data-line-id'), quantity: quantity }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // This is needed as currently there is a bug in Shopify that assumes this header
      }
    }).then(function (cart) {
      cart.json().then(function (content) {
        _this30.itemCount = content['item_count'];
        _this30._rerenderCart(elementToAnimate);

        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      });
    });

    event.preventDefault();
  }
  _reloadPageWithQuantity(event, target) {
    window.location.href = '/cart/change?quantity=' + parseInt(target.value) + '&id=' + target.getAttribute('data-line-id');
  }
  _onProductAdded(event) {
    var _this31 = this;

    this.itemCount += event.detail.quantity;

    this._rerenderCart().then(function () {
      _this31.sidebarDrawer.open();
    });
  }
  _onDrawerClosed() {
    if (this.isCartNoteOpen) {
      this._toggleCartNote();
    }
  }

  /**
   * This method is called internally to rerender the cart, based on the content returned by Shopify Ajax API.
   * We could save some performance by updating directly in JavaScript instead of doing a GET call to get the HTML
   * from Shopify, but by experience, this allows for easier app integration as it allows the Liquid to re-run
   * all the time and hence having easier logic.
   */

  _rerenderCart(elementToAnimate) {
    var _this32 = this;

    // The only thing that we do in JavaScript is checking that if there are no items, we remove the dot in the header
    // and change the cart count in the header
    var cartDot = Dom.nodeListToArray(document.querySelectorAll('.header__cartDot')),
        cartQuantity = Dom.nodeListToArray(document.querySelectorAll('.header__cartCount'));

    cartDot.forEach(function (item) {
      if (_this32.itemCount === 0) {
        item.classList.remove('is-visible'); // IE 11 and lower does not support second attribute of toggle :(
      } else {
        item.classList.add('is-visible');
      }
    });

    cartQuantity.forEach(function (item) {
      item.textContent = _this32.itemCount;
    });

    // Note: appending a timestamp is necessary as the polyfill on IE11 and lower does not support the "cache" property
    return fetch('/cart?view=' + (this.options['drawer'] ? 'drawer' : 'ajax') + '&timestamp=' + Date.now(), {
      credentials: 'same-origin',
      method: 'GET'
    }).then(function (content) {
      // If there is an element to animate, we animate it using a transition
      if (_this32.options['drawer'] && elementToAnimate) {
        var timeline = gsap.timeline({ onComplete: function onComplete() {
            content.text().then(function (html) {
              _this32._replaceContent(html);
            });
          } });

        timeline.to(elementToAnimate, 0.5, { height: 0, opacity: 0, ease: Cubic.easeOut }, 0);

        if (_this32.itemCount === 0) {
          timeline.to(_this32.element.querySelector('.drawer__footer'), 0.5, { y: '100%', transition: 'none', ease: Cubic.easeInOut }, 0);
        }
      } else {
        content.text().then(function (html) {
          _this32._replaceContent(html);
        });
      }
    });
  }
  _replaceContent(html) {
    var tempElement = document.createElement('div');
    tempElement.innerHTML = html;

    // Convert in background
    if (window.theme.currencyConversionEnabled) {
      Currency.convertAll(tempElement);
    }

    var cartNodeParent = this.element.querySelector('.cart').parentNode;

    if (this.options['drawer']) {
      var currentScrollPosition = this.element.querySelector('.drawer__main').scrollTop;
      cartNodeParent.replaceChild(tempElement.querySelector('.cart'), this.element.querySelector('.cart'));
      this.element.querySelector('.drawer__main').scrollTop = currentScrollPosition;
    } else {
      // For dedicated page we replace the whole section if there is no more product
      if (this.itemCount === 0) {
        this.element.innerHTML = tempElement.querySelector('.shopify-section').firstElementChild.innerHTML;
      } else {
        cartNodeParent.replaceChild(tempElement.querySelector('.cart'), this.element.querySelector('.cart'));
        this.element.querySelector('.pageHeader').innerHTML = tempElement.querySelector('.pageHeader').innerHTML;
      }
    }
  }
}